import { DocumentNumberingSettings } from './DocumentNumberingSettings';
import AutomationsPage from '@/features/automation/AutomationsPage';
import { useState, useEffect, useCallback } from 'react';
import { useSearchParams } from 'react-router-dom';
import { useQueryClient } from '@tanstack/react-query';
import { useTranslation } from 'react-i18next';
import { describeApiError } from '@/lib/describeApiError';
import {
  Box,
  Typography,
  Paper,
  Button,
  IconButton,
  Tab,
  Tabs,
  Tooltip,
} from '@mui/material';
import SaveIcon from '@mui/icons-material/Save';
import ArrowBackIcon from '@mui/icons-material/ArrowBack';
import {
  WorkshopSettingsForm,
  NotificationSettingsForm,
} from '@/components/settings';
import { api } from '@/lib/api';
import { queryKeys } from '@/hooks/queryKeys';
import {
  DEFAULT_CONSOLIDATED_INVOICING_FORM,
  consolidatedInvoicingSaveError,
  parseConsolidatedInvoicingForm,
  type ConsolidatedInvoicingFormValue,
  type ConsolidatedInvoicingSaveError,
} from './consolidatedInvoicingForm';
import {
  DEFAULT_PAYMENT_TERMS_DAYS_OPTIONS,
  buildWorkshopSettingsPayload,
  normalizePaymentTermsDaysOptions,
  type WorkshopSettingsSaveGroup,
} from './workshopSettingsPayload';
import { isValidIanaTimeZone } from './timezoneOptions';
import { DEFAULT_WORK_DURATION_STEP_MINUTES } from '@/utils/duration';
import { useNotify } from '@/stores/notificationStore';
import { useAuthStore } from '@/stores/authStore';
import type {
  WorkshopSettings,
  NotificationSettings,
  WorkshopOpeningHours,
  SchedulingType,
  WorkDurationStepMinutes,
  VatRate,
  SmallSuppliesSurcharge,
  AdditionalQuestion,
  ServiceBookLink,
} from '@/types';
import { RolesTab } from './roles/RolesTab';
import { ServicesTab } from './services';
import { InspectionTemplatesTab } from './inspection-templates';
import { ChecklistTemplatesTab } from './checklist-templates';
import { WorkPathTemplatesTab } from './work-path-templates';
import { BrandingTab } from './branding/BrandingTab';
import { DashboardDefaultsSettings } from './DashboardDefaultsSettings';
import { EquipmentManagement } from './equipment/EquipmentManagement';
import { FloorPlanView } from './equipment/FloorPlanView';
import { ParkingManagement } from './equipment/ParkingManagement';
import { ParkingStatusDashboard } from './equipment/ParkingStatusDashboard';
import { LiftStatusDashboard } from './equipment/LiftStatusDashboard';
import { WhatsAppSettingsTab } from './whatsapp/WhatsAppSettingsTab';
import { SveaSettingsTab } from './svea/SveaSettingsTab';
import { MaventaSettingsTab } from './maventa/MaventaSettingsTab';
import { SumUpSettingsTab } from './sumup/SumUpSettingsTab';
import { ResursSettingsTab } from './resurs/ResursSettingsTab';
import { NetvisorSettingsTab } from './netvisor/NetvisorSettingsTab';
import { SmsSettingsTab } from './sms/SmsSettingsTab';
import { InboundEmailSettingsTab } from './integrations-inbound-email';
import { OsBuddyTeachingTab } from './os-buddy/OsBuddyTeachingTab';
import {
  getAllowedSettingsTabs,
  getSettingsTabGroup,
  resolveSettingsTab,
  type SettingsTab,
} from './settingsTabs';
import { SETTINGS_GROUP_LABEL_KEY, SETTINGS_TAB_META } from './settingsTabMeta';
import { SettingsHome } from './SettingsHome';
import { SettingsOnboardingCard } from './SettingsOnboardingCard';
import { IdleTimeoutSettings } from './IdleTimeoutSettings';
import { CourtesyVehiclesTab } from './courtesy';
import { CustomerSignatureSettingsForm } from './workorders-signature';
import { ModuleVisibilitySettings, type ModuleVisibilityFlags } from './ModuleVisibilitySettings';
import { MANAGE_NUMBERING_PERMISSION, MANAGE_WHATSAPP_PERMISSION } from './permissions';
import { idleTimeoutSaveError } from './idleTimeoutValidation';
import {
  DEFAULT_IDLE_LOCK_MINUTES,
  DEFAULT_IDLE_LOGOUT_MINUTES,
  IDLE_LOCK_MINUTES_MAX,
  IDLE_LOCK_MINUTES_MIN,
  IDLE_LOGOUT_MINUTES_MAX,
  IDLE_LOGOUT_MINUTES_MIN,
  isTenantModuleEnabled,
  parseCalendarDefaultView,
  resolveIdleTimeoutSettings,
  useTenantSettings,
} from '@/hooks/useTenantSettings';

export interface InvitationApiResponse {
  id: string;
  email: string;
  role: string;
  firstName: string | null;
  lastName: string | null;
  status: string;
  expiresAt: string;
  createdAt: string;
}

interface TenantSettings {
  timezone?: string;
  currency?: string;
  businessHours?: WorkshopOpeningHours;
  address?: string;
  city?: string;
  postalCode?: string;
  country?: string;
  businessId?: string;
  vatNumber?: string;
  laborRateCents?: number;
  laborRateGrossCents?: number;
  /** Workshop-wide default (cents) for the pre-approved additional-work limit (#296). */
  defaultAdditionalWorkLimitCents?: number;
  /** Annual late-payment interest (%) printed on invoices (#1752). */
  lateInterestRatePercent?: number;
  /** Selectable net payment terms in days for the invoice dialog (#1997). */
  paymentTermsDaysOptions?: number[];
  /** Seller bank details required before an invoice can be finalized (#1035). */
  bankIban?: string;
  bankBic?: string;
  defaultPickupBufferMinutes?: number;
  workDurationStepMinutes?: WorkDurationStepMinutes;
  /** Stored raw; read through parseCalendarDefaultView (#4121). */
  calendarDefaultView?: unknown;
  serviceBookLinks?: ServiceBookLink[];
  additionalQuestions?: AdditionalQuestion[];
  smallSuppliesSurcharge?: SmallSuppliesSurcharge;
  vatRates?: VatRate[];
  defaultVatRateId?: string | null;
  contactEmail?: string;
  contactPhone?: string;
  notificationSettings?: NotificationSettings;
  schedulingLookaheadDays?: number;
  defaultSchedulingType?: SchedulingType;
  defaultQuoteValidityDays?: number;
  workdayUnpaidLunchMinutes?: number;
  /** Workshop-wide idle lock / logout (#2216). */
  security?: {
    idleLockMinutes?: number;
    idleLogoutMinutes?: number;
  };
  /** Module visibility (#2646). Missing keys stay on. */
  features?: {
    inspectionsEnabled?: boolean;
    serviceBookEnabled?: boolean;
    partsOrderingEnabled?: boolean;
  };
  /** Consolidated invoicing schedule (#3929). Absent until the workshop sets it. */
  consolidatedInvoicing?: unknown;
}

const CONSOLIDATED_SCHEDULE_ERROR_KEY: Record<ConsolidatedInvoicingSaveError, string> = {
  cutoff: 'consolidatedInvoicing.errors.cutoff',
  weekday: 'consolidatedInvoicing.errors.weekday',
  anchor: 'consolidatedInvoicing.errors.anchor',
};

interface TenantResponse {
  id: string;
  name: string;
  settings: TenantSettings;
}

const defaultFinnishVatRates: VatRate[] = [
  { id: 'fi-25.5', label: '25,5 %', percentage: 25.5 },
  { id: 'fi-14', label: '14 %', percentage: 14 },
  { id: 'fi-10', label: '10 %', percentage: 10 },
  { id: 'fi-0', label: '0 %', percentage: 0 },
];

const defaultWorkshopSettings: WorkshopSettings = {
  name: '',
  address: '',
  city: '',
  postalCode: '',
  country: 'Finland',
  phone: '',
  email: '',
  businessId: '',
  vatNumber: '',
  timezone: 'Europe/Helsinki',
  currency: 'EUR',
  laborRateCents: 0,
  vatRates: defaultFinnishVatRates,
  defaultVatRateId: 'fi-25.5',
  schedulingLookaheadDays: 4,
  // A workshop that has never touched the setting schedules "during the day",
  // which is what most work actually is (#1931).
  defaultSchedulingType: 'floating',
  defaultQuoteValidityDays: 14,
  workdayUnpaidLunchMinutes: 30,
  openingHours: {
    monday: { open: '08:00', close: '17:00' },
    tuesday: { open: '08:00', close: '17:00' },
    wednesday: { open: '08:00', close: '17:00' },
    thursday: { open: '08:00', close: '17:00' },
    friday: { open: '08:00', close: '17:00' },
    saturday: null,
    sunday: null,
  },
};

const defaultNotificationSettings: NotificationSettings = {
  emailNotifications: true,
  smsNotifications: false,
  pushNotifications: true,
  newBookingAlert: true,
  approvalReceivedAlert: true,
  customerMessageAlert: true,
  dailySummaryEmail: false,
  vehicleReadyCustomerEmail: false,
  vehicleReadyTrigger: 'ready_to_invoice',
  vehicleReadyCustomerWhatsApp: false,
  quoteDecisionTaskEnabled: false,
};

export default function SettingsPage() {
  const { t } = useTranslation('settings');
  const { t: tc } = useTranslation('common');
  const notify = useNotify();
  const queryClient = useQueryClient();
  const hasPermission = useAuthStore((s) => s.hasPermission);

  // Settings consolidates workshop configuration, templates, roles and
  // (since issue #1012) the former Equipment page's sections into a single
  // page with URL-driven tabs (?tab=…) so every tab stays deep-linkable. The
  // active tab is clamped to the tabs the user may actually see, so a
  // crafted ?tab= for a permission the user lacks can never leave the MUI
  // Tabs component on an unmatched value (blank content).
  const [searchParams, setSearchParams] = useSearchParams();
  const canViewSettings = hasPermission('settings.view');
  const canReadAutomations = hasPermission('automation.rules.read');
  const canManageBranding = hasPermission('settings.manage_branding');
  const canEditIdleSettings = hasPermission('settings.edit_workshop');
  const canManageDashboardDefaults = hasPermission('settings.manage_users');
  const canManageWhatsApp = hasPermission(MANAGE_WHATSAPP_PERMISSION);
  const canManageNumbering = hasPermission(MANAGE_NUMBERING_PERMISSION);
  const canTeachOsBuddy = hasPermission('assistant.teach');
  const { inspectionsEnabled, serviceBookEnabled } = useTenantSettings();
  const allowedTabs = getAllowedSettingsTabs({
    canViewSettings,
    canReadAutomations,
    canManageBranding,
    canManageNumbering,
    canManageDashboardDefaults,
    canManageWhatsApp,
    canTeachOsBuddy,
    canEditIdleSettings,
    // Same permission as the workshop identity it sends to Svea (#2806).
    canRequestSveaContact: canEditIdleSettings,
    // Same permission as the Svea credentials (#3068).
    canManageEInvoicing: canEditIdleSettings,
    // Same permission as the Svea credentials (#3498).
    canManageCardTerminals: canEditIdleSettings,
    // Same permission as the Svea credentials (#1457).
    canManageResurs: canEditIdleSettings,
    // Same permission as the other integrations that act in the workshop's name (#3022, #3023).
    canManageAccounting: canEditIdleSettings,
    canManageSms: canEditIdleSettings,
    inspectionsEnabled,
    serviceBookEnabled,
    canManageEquipment: hasPermission('equipment.manage'),
  });
  // No ?tab= means the landing page (#2425). Every setting, including
  // 'workshop', now carries its own parameter — the bare URL is the map, not a
  // shortcut into the first form.
  const requestedTab = searchParams.get('tab');
  const activeTab = requestedTab === null
    ? null
    : resolveSettingsTab(requestedTab, allowedTabs);

  /**
   * The other settings in the same card, so the open page can move sideways
   * instead of making the user back out to the landing page (#2618 follow-up).
   * Permission-filtered, so a tab the user cannot see never appears here.
   */
  const groupTabs =
    activeTab === null
      ? []
      : allowedTabs.filter((tab) => getSettingsTabGroup(tab) === getSettingsTabGroup(activeTab));

  const openTab = useCallback((value: SettingsTab) => {
    const next = new URLSearchParams(searchParams);
    next.set('tab', value);
    if (value !== 'automations') {
      next.delete('create');
      next.delete('insightId');
      next.delete('insightType');
    }
    // A push, not a replace: opening a setting is a navigation, so Back has to
    // return to the landing page rather than leaving Settings altogether.
    setSearchParams(next);
  }, [searchParams, setSearchParams]);

  const backToHome = useCallback(() => {
    const next = new URLSearchParams(searchParams);
    next.delete('tab');
    next.delete('create');
    next.delete('insightId');
    next.delete('insightType');
    setSearchParams(next);
  }, [searchParams, setSearchParams]);

  const [workshopSettings, setWorkshopSettings] = useState<WorkshopSettings>(defaultWorkshopSettings);
  const [paymentTermsDaysOptions, setPaymentTermsDaysOptions] = useState<number[]>(
    () => [...DEFAULT_PAYMENT_TERMS_DAYS_OPTIONS],
  );
  const [consolidatedInvoicing, setConsolidatedInvoicing] = useState<ConsolidatedInvoicingFormValue>(
    DEFAULT_CONSOLIDATED_INVOICING_FORM,
  );
  const [notificationSettings, setNotificationSettings] = useState<NotificationSettings>(defaultNotificationSettings);
  const [idleLockMinutes, setIdleLockMinutes] = useState(DEFAULT_IDLE_LOCK_MINUTES);
  const [idleLogoutMinutes, setIdleLogoutMinutes] = useState(DEFAULT_IDLE_LOGOUT_MINUTES);
  const [moduleFlags, setModuleFlags] = useState<ModuleVisibilityFlags>({
    inspectionsEnabled: true,
    serviceBookEnabled: true,
    partsOrderingEnabled: true,
  });
  const [savingWorkshop, setSavingWorkshop] = useState(false);
  const [savingSecurity, setSavingSecurity] = useState(false);
  const [savingNotifications, setSavingNotifications] = useState(false);
  const [savingModules, setSavingModules] = useState(false);

  const idlePairError = idleTimeoutSaveError(
    canEditIdleSettings,
    idleLockMinutes,
    idleLogoutMinutes,
  );

  // Load tenant settings (workshop + notifications) from API on mount
  useEffect(() => {
    if (!canViewSettings) return;
    async function loadTenantSettings() {
      try {
        const tenant = await api.get<TenantResponse>('/tenants/me');
        const s = tenant.settings;
        setWorkshopSettings({
          name: tenant.name,
          address: s.address ?? '',
          city: s.city ?? '',
          postalCode: s.postalCode ?? '',
          country: s.country ?? 'Finland',
          phone: s.contactPhone ?? '',
          email: s.contactEmail ?? '',
          businessId: s.businessId ?? '',
          vatNumber: s.vatNumber ?? '',
          timezone: s.timezone ?? 'Europe/Helsinki',
          currency: s.currency ?? 'EUR',
          laborRateCents: s.laborRateCents ?? 0,
          laborRateGrossCents: s.laborRateGrossCents,
          defaultAdditionalWorkLimitCents: s.defaultAdditionalWorkLimitCents,
          lateInterestRatePercent: s.lateInterestRatePercent,
          bankIban: s.bankIban ?? '',
          bankBic: s.bankBic ?? '',
          smallSuppliesSurcharge: s.smallSuppliesSurcharge,
          vatRates: s.vatRates ?? defaultFinnishVatRates,
          defaultVatRateId: s.defaultVatRateId ?? 'fi-25.5',
          schedulingLookaheadDays: s.schedulingLookaheadDays ?? 4,
          defaultSchedulingType: s.defaultSchedulingType ?? 'floating',
          defaultQuoteValidityDays: s.defaultQuoteValidityDays ?? 14,
          workdayUnpaidLunchMinutes: s.workdayUnpaidLunchMinutes ?? 30,
          openingHours: s.businessHours ?? defaultWorkshopSettings.openingHours,
          // Never loaded before (#2618), so the page showed empty lists and a
          // default buffer even when the tenant had values stored.
          defaultPickupBufferMinutes: s.defaultPickupBufferMinutes,
          workDurationStepMinutes: s.workDurationStepMinutes ?? DEFAULT_WORK_DURATION_STEP_MINUTES,
          // 'day' when never set or unreadable, so Save never writes a value the API rejects (#4121).
          calendarDefaultView: parseCalendarDefaultView(s.calendarDefaultView),
          serviceBookLinks: s.serviceBookLinks ?? [],
          additionalQuestions: s.additionalQuestions ?? [],
        });
        setPaymentTermsDaysOptions(normalizePaymentTermsDaysOptions(s.paymentTermsDaysOptions));
        setConsolidatedInvoicing(parseConsolidatedInvoicingForm(s.consolidatedInvoicing));
        const idle = resolveIdleTimeoutSettings(s.security);
        setIdleLockMinutes(idle.idleLockMinutes);
        setIdleLogoutMinutes(idle.idleLogoutMinutes);
        if (s.notificationSettings) {
          // Stored blobs written before a switch existed lack its key: keep the default.
          setNotificationSettings({ ...defaultNotificationSettings, ...s.notificationSettings });
        }
        setModuleFlags({
          inspectionsEnabled: isTenantModuleEnabled(s.features?.inspectionsEnabled),
          serviceBookEnabled: isTenantModuleEnabled(s.features?.serviceBookEnabled),
          partsOrderingEnabled: isTenantModuleEnabled(s.features?.partsOrderingEnabled),
        });
      } catch {
        // Keep defaults on error
      }
    }
    void loadTenantSettings();
  }, [canViewSettings]);

  const workshopSaveGroup: WorkshopSettingsSaveGroup | null =
    activeTab === 'workshop'
      ? 'identity'
      : activeTab === 'workshop-pricing'
        ? 'pricing'
        : activeTab === 'workshop-hours'
          ? 'hours'
          : null;

  const consolidatedScheduleError =
    workshopSaveGroup === 'pricing' ? consolidatedInvoicingSaveError(consolidatedInvoicing) : null;

  const handleSaveWorkshopGroup = async (group: WorkshopSettingsSaveGroup) => {
    if (group === 'pricing' && paymentTermsDaysOptions.length === 0) {
      notify.error(t('error'), t('paymentTerms.emptyError'));
      return;
    }
    if (group === 'pricing') {
      const scheduleError = consolidatedInvoicingSaveError(consolidatedInvoicing);
      if (scheduleError !== null) {
        notify.error(t('error'), t(CONSOLIDATED_SCHEDULE_ERROR_KEY[scheduleError]));
        return;
      }
    }
    if (group === 'identity' && !isValidIanaTimeZone(workshopSettings.timezone)) {
      notify.error(t('error'), t('timezone.invalid'));
      return;
    }
    setSavingWorkshop(true);
    try {
      // Tenant name is a column, not a settings key. Only the identity page
      // edits it, so the other saves must not PATCH /tenants/me (#2428).
      if (group === 'identity' && workshopSettings.name) {
        await api.patch('/tenants/me', { name: workshopSettings.name });
      }
      // Each group PATCHes only the keys it owns. A full workshop payload
      // would write stale pricing over a change saved on the other page.
      const updated = await api.patch<TenantResponse>(
        '/tenants/me/settings',
        buildWorkshopSettingsPayload(
          workshopSettings,
          group === 'pricing' ? paymentTermsDaysOptions : undefined,
          group,
          group === 'pricing' ? consolidatedInvoicing : undefined,
        ),
      );
      if (group === 'pricing') {
        // The API recomputes the net rate from the gross one, so take its
        // answer instead of leaving the pre-save value in local state.
        const saved = updated.settings;
        setWorkshopSettings((prev) => ({
          ...prev,
          laborRateCents: saved.laborRateCents ?? prev.laborRateCents,
          // Always recomputed from the stored net rate, never persisted (#1708).
          laborRateGrossCents: saved.laborRateGrossCents,
        }));
      }
      await queryClient.invalidateQueries({ queryKey: queryKeys.tenant.all });
      notify.success(t('settingsSaved'), t('workshopSettingsSaved'));
    } catch (err) {
      console.error('Workshop settings save failed:', err);
      notify.error(t('error'), describeApiError(err, tc, { fallback: t('saveFailed') }));
    } finally {
      setSavingWorkshop(false);
    }
  };

  /**
   * Session lock and logout (#2463). These rode the workshop save until they
   * moved to their own tab; the permission gate is unchanged, and the route
   * replaces the whole `security` key, so the object is sent complete.
   */
  const handleSaveSecurity = async () => {
    if (idlePairError === 'logoutMustExceedLock') {
      notify.error(t('error'), t('idleTimeout.logoutMustExceedLock'));
      return;
    }
    if (idlePairError === 'lockRange') {
      notify.error(
        t('error'),
        t('idleTimeout.lockRange', { min: IDLE_LOCK_MINUTES_MIN, max: IDLE_LOCK_MINUTES_MAX }),
      );
      return;
    }
    if (idlePairError === 'logoutRange') {
      notify.error(
        t('error'),
        t('idleTimeout.logoutRange', { min: IDLE_LOGOUT_MINUTES_MIN, max: IDLE_LOGOUT_MINUTES_MAX }),
      );
      return;
    }
    setSavingSecurity(true);
    try {
      await api.patch('/tenants/me/settings', {
        security: { idleLockMinutes, idleLogoutMinutes },
      });
      await queryClient.invalidateQueries({ queryKey: queryKeys.tenant.all });
      notify.success(t('settingsSaved'), t('workshopSettingsSaved'));
    } catch (err) {
      console.error('Security settings save failed:', err);
      notify.error(t('error'), describeApiError(err, tc, { fallback: t('saveFailed') }));
    } finally {
      setSavingSecurity(false);
    }
  };

  /**
   * Module visibility (#2646). Sends only the three switches so WhatsApp and
   * other stored feature flags survive the nested deep merge.
   */
  const handleSaveModules = async () => {
    setSavingModules(true);
    try {
      await api.patch('/tenants/me/settings', {
        features: {
          inspectionsEnabled: moduleFlags.inspectionsEnabled,
          serviceBookEnabled: moduleFlags.serviceBookEnabled,
          partsOrderingEnabled: moduleFlags.partsOrderingEnabled,
        },
      });
      await queryClient.invalidateQueries({ queryKey: queryKeys.tenant.all });
      notify.success(t('settingsSaved'), t('modules.savedDesc'));
    } catch (err) {
      console.error('Module visibility save failed:', err);
      notify.error(t('error'), describeApiError(err, tc, { fallback: t('saveFailed') }));
    } finally {
      setSavingModules(false);
    }
  };

  const handleSaveNotifications = async () => {
    setSavingNotifications(true);
    try {
      await api.patch('/tenants/me/settings', {
        notificationSettings,
      });
      notify.success(t('settingsSaved'), t('notificationSettingsSaved'));
    } catch (error) {
      notify.error(t('error'), describeApiError(error, tc, { fallback: t('saveFailed') }));
    } finally {
      setSavingNotifications(false);
    }
  };

  return (
    <Box>
      <Box display="flex" alignItems="center" gap={1} mb={2}>
        {activeTab !== null && (
          <Tooltip title={tc('back')}>
            <IconButton onClick={backToHome} aria-label={tc('back')} size="small">
              <ArrowBackIcon />
            </IconButton>
          </Tooltip>
        )}
        <Box>
          {/*
            The heading names the GROUP once a card is open, not the single
            setting: a card opens a group, and the tab row below says which
            part of it you are on. Before this the only way from Notifications
            to Numbering was back out to the landing page and in again.
          */}
          <Typography variant="h4" fontWeight={600}>
            {activeTab === null
              ? t('title')
              : t(SETTINGS_GROUP_LABEL_KEY[getSettingsTabGroup(activeTab)])}
          </Typography>
        </Box>
      </Box>

      {activeTab !== null && groupTabs.length > 1 && (
        <Box sx={{ borderBottom: 1, borderColor: 'divider', mb: 3 }}>
          <Tabs
            value={activeTab}
            onChange={(_e, value: SettingsTab) => openTab(value)}
            variant="scrollable"
            scrollButtons="auto"
            data-testid="settings-group-tabs"
          >
            {groupTabs.map((tab) => (
              <Tab
                key={tab}
                value={tab}
                label={t(SETTINGS_TAB_META[tab].labelKey)}
                data-testid={`settings-group-tab-${tab}`}
              />
            ))}
          </Tabs>
        </Box>
      )}

      {activeTab === null && (
        <>
          {/*
            The wizard summary and the global reminder both point here, so
            this page has to say which setting they meant (#2497).
          */}
          {canViewSettings && <SettingsOnboardingCard settings={workshopSettings} onOpen={openTab} />}
          <SettingsHome allowedTabs={allowedTabs} onOpen={openTab} />
        </>
      )}

      {activeTab !== null && (
      <Paper elevation={0} sx={{ border: 1, borderColor: 'divider', borderRadius: 2 }}>

        <Box sx={{ p: 3 }}>
          {workshopSaveGroup !== null && (
            <>
              <WorkshopSettingsForm
                settings={workshopSettings}
                onChange={setWorkshopSettings}
                paymentTermsDaysOptions={paymentTermsDaysOptions}
                onPaymentTermsChange={setPaymentTermsDaysOptions}
                consolidatedInvoicing={consolidatedInvoicing}
                onConsolidatedInvoicingChange={setConsolidatedInvoicing}
                group={workshopSaveGroup}
              />
              <Box sx={{ mt: 4, display: 'flex', justifyContent: 'flex-end' }}>
                <Button
                  variant="contained"
                  startIcon={<SaveIcon />}
                  onClick={() => void handleSaveWorkshopGroup(workshopSaveGroup)}
                  disabled={
                    savingWorkshop
                    || (workshopSaveGroup === 'pricing' && paymentTermsDaysOptions.length === 0)
                    || consolidatedScheduleError !== null
                    || (workshopSaveGroup === 'identity' && !isValidIanaTimeZone(workshopSettings.timezone))
                  }
                >
                  {savingWorkshop ? t('saving') : t('saveChanges')}
                </Button>
              </Box>
            </>
          )}

          {activeTab === 'numbering' && canManageNumbering && <DocumentNumberingSettings />}

          {activeTab === 'security' && canEditIdleSettings && (
            <>
              <IdleTimeoutSettings
                idleLockMinutes={idleLockMinutes}
                idleLogoutMinutes={idleLogoutMinutes}
                onChange={(next) => {
                  setIdleLockMinutes(next.idleLockMinutes);
                  setIdleLogoutMinutes(next.idleLogoutMinutes);
                }}
              />
              <Box sx={{ mt: 4, display: 'flex', justifyContent: 'flex-end' }}>
                <Button
                  variant="contained"
                  startIcon={<SaveIcon />}
                  onClick={() => void handleSaveSecurity()}
                  disabled={savingSecurity || idlePairError !== null}
                >
                  {savingSecurity ? t('saving') : t('saveChanges')}
                </Button>
              </Box>
            </>
          )}

          {activeTab === 'workorders-signature' && <CustomerSignatureSettingsForm />}

          {activeTab === 'modules' && (
            <>
              <ModuleVisibilitySettings value={moduleFlags} onChange={setModuleFlags} />
              <Box sx={{ mt: 4, display: 'flex', justifyContent: 'flex-end' }}>
                <Button
                  variant="contained"
                  startIcon={<SaveIcon />}
                  onClick={() => void handleSaveModules()}
                  disabled={savingModules}
                >
                  {savingModules ? t('saving') : t('saveChanges')}
                </Button>
              </Box>
            </>
          )}

          {activeTab === 'notifications' && (
            <>
              <NotificationSettingsForm
                settings={notificationSettings}
                onChange={setNotificationSettings}
              />
              <Box sx={{ mt: 4, display: 'flex', justifyContent: 'flex-end' }}>
                <Button
                  variant="contained"
                  startIcon={<SaveIcon />}
                  onClick={() => void handleSaveNotifications()}
                  disabled={savingNotifications}
                >
                  {savingNotifications ? t('saving') : t('saveChanges')}
                </Button>
              </Box>
            </>
          )}

          {activeTab === 'services' && <ServicesTab />}

          {activeTab === 'inspection-templates' && <InspectionTemplatesTab />}

          {activeTab === 'checklist-templates' && <ChecklistTemplatesTab />}

          {activeTab === 'work-paths' && <WorkPathTemplatesTab />}

          {activeTab === 'automations' && canReadAutomations && <AutomationsPage embedded />}

          {activeTab === 'roles' && <RolesTab />}

          {activeTab === 'os-buddy' && canTeachOsBuddy && <OsBuddyTeachingTab />}

          {activeTab === 'dashboard-defaults' && canManageDashboardDefaults && (
            <DashboardDefaultsSettings />
          )}

          {activeTab === 'branding' && canManageBranding && <BrandingTab />}

          {activeTab === 'whatsapp' && canManageWhatsApp && <WhatsAppSettingsTab />}
          {activeTab === 'svea' && canEditIdleSettings && <SveaSettingsTab />}
          {activeTab === 'sumup' && canEditIdleSettings && <SumUpSettingsTab />}
          {activeTab === 'maventa' && canEditIdleSettings && <MaventaSettingsTab />}
          {activeTab === 'resurs' && canEditIdleSettings && <ResursSettingsTab />}
          {activeTab === 'inbound-email' && <InboundEmailSettingsTab />}
          {activeTab === 'netvisor' && canEditIdleSettings && <NetvisorSettingsTab />}
          {activeTab === 'sms' && canEditIdleSettings && <SmsSettingsTab />}

          {activeTab === 'equipment' && <EquipmentManagement />}

          {activeTab === 'floor-plan' && <FloorPlanView />}

          {activeTab === 'parking' && (
            <>
              <ParkingStatusDashboard />
              <Box sx={{ mt: 4 }}>
                <ParkingManagement />
              </Box>
            </>
          )}

          {activeTab === 'lift-status' && <LiftStatusDashboard />}

          {activeTab === 'courtesy' && <CourtesyVehiclesTab />}
        </Box>
      </Paper>
      )}
    </Box>
  );
}
